'use client'

import { useState, useEffect } from 'react'
import { Save, ChevronDown } from 'lucide-react'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import type { SceneInput } from '@creanizer/types'
import { useCustomValuesStore } from '../../../../stores/customValuesStore'

interface SceneFormOutlineProps {
  input: SceneInput
  onChange: (input: SceneInput) => void
}

export function SceneFormOutline({ input, onChange }: SceneFormOutlineProps) {
  const [showCustomGenres, setShowCustomGenres] = useState(false)
  
  const { 
    customValues, 
    addCustomValue, 
    getCustomValues, 
    useCustomValue,
    init 
  } = useCustomValuesStore()
  
  // カスタム値ストアの初期化
  useEffect(() => {
    init()
  }, [])
  
  // カスタム値を保存
  const saveCustomValue = async (type: 'genre', value: string, label: string) => {
    try {
      await addCustomValue(type, value, label, 'scene')
      alert(`${label}を保存しました`)
    } catch (error) {
      console.error('Failed to save custom value:', error)
      alert('保存に失敗しました')
    }
  }
  
  // カスタム値を選択して使用
  const selectCustomValue = async (type: 'genre', value: any) => {
    // 使用回数を更新
    await useCustomValue(value.id)
    
    // 値を設定
    if (type === 'genre') {
      onChange({ ...input, genre: 'custom', customGenre: value.value })
    }
  }
  
  return (
    <div className="space-y-4">
      {/* アウトラインテキスト */}
      <div>
        <Label htmlFor="outlineText">シーンのアウトライン</Label>
        <Textarea
          id="outlineText"
          value={input.outlineText || ''}
          onChange={(e) => onChange({ ...input, outlineText: e.target.value })}
          placeholder="例：
1. 主人公が古い図書館に到着
2. 謎の司書と出会う
3. 禁書の存在を知る
4. 地下書庫への侵入を決意
5. 警備を潜り抜ける緊張感のあるシーン"
          rows={10}
        />
        <p className="text-xs text-muted-foreground mt-1">
          箇条書きやメモ形式で、シーンの流れを記述してください
        </p>
      </div>

      {/* 詳細度 */}
      <div>
        <Label htmlFor="outlineDetail">生成の詳細度</Label>
        <Select
          value={input.outlineDetail || 'standard'}
          onValueChange={(value) => onChange({ ...input, outlineDetail: value as any })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="minimal">
              簡潔に（アウトラインの要点のみ展開）
            </SelectItem>
            <SelectItem value="standard">
              標準的に（バランスよく肉付け）
            </SelectItem>
            <SelectItem value="detailed">
              詳細に（細部まで豊かに描写）
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ジャンル選択 */}
      <div>
        <Label htmlFor="genre">ジャンル</Label>
        <Select
          value={input.genre || 'modern'}
          onValueChange={(value) => onChange({ ...input, genre: value as any })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fantasy">ファンタジー</SelectItem>
            <SelectItem value="sf">SF</SelectItem>
            <SelectItem value="modern">現代</SelectItem>
            <SelectItem value="mystery">ミステリー</SelectItem>
            <SelectItem value="romance">ロマンス</SelectItem>
            <SelectItem value="horror">ホラー</SelectItem>
            <SelectItem value="historical">歴史</SelectItem>
            <SelectItem value="comedy">コメディ</SelectItem>
            <SelectItem value="literary">文学</SelectItem>
            <SelectItem value="custom">カスタム</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* カスタムジャンル */}
      {input.genre === 'custom' && (
        <div className="space-y-2">
          <Label htmlFor="customGenre">カスタムジャンル</Label>
          <div className="flex gap-2">
            <Input
              id="customGenre"
              value={input.customGenre || ''}
              onChange={(e) => onChange({ ...input, customGenre: e.target.value })}
              placeholder="独自のジャンルを入力"
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                if (input.customGenre) {
                  saveCustomValue('genre', input.customGenre, input.customGenre)
                }
              }}
              disabled={!input.customGenre}
            >
              <Save className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowCustomGenres(!showCustomGenres)}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          {showCustomGenres && (
            <div className="border rounded p-2 max-h-32 overflow-y-auto">
              {getCustomValues('genre', 'scene').length === 0 ? (
                <p className="text-sm text-gray-500">保存されたジャンルはありません</p>
              ) : (
                <div className="space-y-1">
                  {getCustomValues('genre', 'scene').map((value) => (
                    <button
                      key={value.id}
                      type="button"
                      className="block w-full text-left p-1 hover:bg-gray-100 rounded text-sm"
                      onClick={() => selectCustomValue('genre', value)}
                    >
                      {value.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 追加設定 */}
      <div>
        <Label htmlFor="additionalSettings">追加の指示（オプション）</Label>
        <Textarea
          id="additionalSettings"
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          placeholder="アウトラインを展開する際の特別な要望や注意点"
          rows={3}
        />
      </div>

      {/* ヒント */}
      <Alert>
        <AlertDescription>
          <strong>アウトラインモードのコツ：</strong>
          <ul className="mt-2 ml-4 list-disc text-sm">
            <li>箇条書きで要点を整理すると、AIが理解しやすくなります</li>
            <li>時系列順に並べると、自然な流れのシーンが生成されます</li>
            <li>感情や雰囲気のメモを含めると、より豊かな描写になります</li>
            <li>「詳細度」で、どの程度肉付けするかコントロールできます</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  )
}